/**
 * Blog posts list hook.
 *
 * Fallback chain:
 *   1. Live API (FastAPI backend)
 *   2. Blog snapshot (/snapshot/blog/blog-posts.json - auto-generated by backend)
 *   3. Empty array (no blogs to show)
 *
 * No hardcoded fallback. The snapshot is the source of truth when the API is down.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchPublicBlogPostsRaw } from "@/exxonim/services/blogService";
import { fetchWithBlogSnapshotFallback } from "@/exxonim/services/staticFallbackService";
import { mapBlogPost } from "@/exxonim/utils/contentMappers";
import type { BlogPost } from "@/exxonim/types";

export function useBlogPosts() {
  const query = useQuery<BlogPost[]>({
    queryKey: ["blog", "posts"],
    queryFn: async () => {
      const raw = await fetchWithBlogSnapshotFallback(fetchPublicBlogPostsRaw, "blog-posts");
      const items = Array.isArray(raw) ? raw : raw?.items ?? [];
      return items.map(mapBlogPost);
    },
    staleTime: 1000 * 30, // 30 seconds - blog updates propagate fast
    retry: 1,
  });

  return {
    ...query,
    data: query.data ?? [],
    isPending: query.isPending,
  };
}
